import { ChevronDownIcon, ChevronRightIcon, GitPullRequestIcon, VerifiedIcon, WorkflowIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCiRun } from "../api/hooks";
import type { CiRun as Run, Evidence } from "../api/types";
import { Query, Sha, TimeAgo } from "../components/common";
import { duration, safeJson } from "../components/format";
import { EvidenceIcon, StateLabel } from "../components/StateLabel";
import { Card, CardHeader } from "../components/ui/card";
import { ListItemSeparator } from "../components/ui/list";
import { Tooltip } from "../components/ui/tooltip";
import { cn } from "../lib/utils";

type Status = Evidence["status"];

/** One card in the graph: a job, or a matrix job with all its instances. */
interface Node {
  id: string;
  name: string;
  jobs: Evidence[];
  matrix: boolean;
  status: Status;
  needs: string[]; // node ids
  depth: number;
}

const failed = (s: Status) => s === "fail" || s === "infra_error";
export const logHref = (repo: string, e: Evidence) =>
  `${rp(repo)}/ci/log?change=${encodeURIComponent(e.change_id)}&job=${encodeURIComponent(e.job)}&attempt=${e.attempt}`;
export const runHref = (repo: string, subject: string, commit: string) => `${rp(repo)}/ci/runs/${encodeURIComponent(subject)}/${commit}`;

export function rollup(jobs: Evidence[]): Status {
  if (jobs.some((e) => failed(e.status))) return "fail";
  if (jobs.some((e) => e.status === "queued")) return "queued";
  if (jobs.some((e) => e.status === "waiting")) return "waiting";
  if (jobs.length && jobs.every((e) => e.status === "skipped")) return "skipped";
  return "pass";
}

const STATUS_WORD: Record<Status, string> = {
  waiting: "waiting",
  queued: "running",
  pass: "passed",
  fail: "failed",
  infra_error: "infra error",
  skipped: "skipped",
};

function blockedBy(e: Evidence): string | null {
  const s = safeJson(e.summary);
  return s && typeof s === "object" && "blocked_by" in s ? String((s as { blocked_by: unknown }).blocked_by) : null;
}

/** The runs_on labels no live runner offers, for a queued job the housekeeper flagged. */
function waitingFor(e: Evidence): string[] | null {
  if (e.status !== "queued") return null;
  const s = safeJson(e.summary) as { waiting_for?: { labels?: unknown } } | null;
  const labels = s && typeof s === "object" ? s.waiting_for?.labels : null;
  return Array.isArray(labels) ? labels.map(String) : null;
}

/** Groups jobs into nodes, links them by their needs and gives each a depth. */
function toNodes(jobs: Evidence[]): Node[] {
  const nodes = new Map<string, Node>();
  const nodeOf = new Map<string, string>();
  for (const e of jobs) {
    const id = e.job_group ?? e.job;
    let n = nodes.get(id);
    if (!n) nodes.set(id, (n = { id, name: id, jobs: [], matrix: e.job_group !== null, status: "pass", needs: [], depth: 0 }));
    n.jobs.push(e);
    nodeOf.set(e.job, id);
  }
  for (const n of nodes.values()) {
    n.status = rollup(n.jobs);
    n.needs = [
      ...new Set(
        n.jobs
          .flatMap((e) => e.needs ?? [])
          .map((j) => nodeOf.get(j))
          .filter((id): id is string => !!id && id !== n.id),
      ),
    ];
  }
  const above = new Map<string, Set<string>>();
  const ancestors = (id: string, seen = new Set<string>()): Set<string> => {
    const hit = above.get(id);
    if (hit) return hit;
    const out = new Set<string>();
    if (seen.has(id)) return out;
    seen.add(id);
    for (const p of nodes.get(id)!.needs) {
      out.add(p);
      for (const a of ancestors(p, seen)) out.add(a);
    }
    above.set(id, out);
    return out;
  };
  // Drop edges implied by a longer path, so release gates don't fan in from every column.
  for (const n of nodes.values()) n.needs = n.needs.filter((a) => !n.needs.some((b) => b !== a && ancestors(b).has(a)));
  const depth = (n: Node, seen = new Set<string>()): number => {
    if (seen.has(n.id)) return 0;
    seen.add(n.id);
    return n.needs.length ? 1 + Math.max(...n.needs.map((id) => depth(nodes.get(id)!, seen))) : 0;
  };
  for (const n of nodes.values()) n.depth = depth(n);
  return [...nodes.values()];
}

const MIN_W = 168;
const MAX_W = 232;
const GAP_X = 40;
const HEAD = 40;
const ROW = 30;
const GAP_Y = 12;

interface Placed extends Node {
  x: number;
  y: number;
  h: number;
}

interface Layout {
  placed: Placed[];
  w: number;
  width: number;
  height: number;
}

/**
 * Columns are dependency depth. Cards shrink (down to MIN_W) to fit `avail`; within a
 * column, each node centres on the nodes it needs.
 */
function layout(nodes: Node[], open: Set<string>, avail: number): Layout {
  const ncols = 1 + Math.max(0, ...nodes.map((n) => n.depth));
  const w = Math.max(MIN_W, Math.min(MAX_W, Math.floor((avail - (ncols - 1) * GAP_X) / ncols)));
  const placed = new Map<string, Placed>();
  let x = 0;
  let height = 0;
  for (let d = 0; d < ncols; d++) {
    const col = nodes.filter((n) => n.depth === d);
    const center = (n: Node) => {
      const ys = n.needs
        .map((id) => placed.get(id))
        .filter((p): p is Placed => !!p)
        .map((p) => p.y + HEAD / 2);
      return ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : null;
    };
    col.sort((a, b) => (center(a) ?? 0) - (center(b) ?? 0) || a.name.localeCompare(b.name));
    let y = 0;
    for (const n of col) {
      const h = HEAD + (n.matrix && open.has(n.id) ? n.jobs.length * ROW + 8 : 0);
      const c = center(n);
      const top = c === null ? y : Math.max(y, c - HEAD / 2);
      placed.set(n.id, { ...n, x, y: top, h });
      y = top + h + GAP_Y;
    }
    height = Math.max(height, y - GAP_Y);
    x += w + GAP_X;
  }
  return { placed: [...placed.values()], w, width: x - GAP_X, height };
}

/** Width of an element, tracked across resizes. */
function useWidth<T extends HTMLElement>(): [(el: T | null) => void, number] {
  const [width, setWidth] = useState(0);
  const ref = useCallback((el: T | null) => {
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const EDGE_CLASS: Record<Status, string> = {
  pass: "stroke-success-muted",
  fail: "stroke-danger-muted",
  infra_error: "stroke-danger-muted",
  skipped: "stroke-border [stroke-dasharray:4_3]",
  waiting: "stroke-border [stroke-dasharray:4_3]",
  queued: "stroke-warning-muted [stroke-dasharray:4_3]",
};

function Graph({ repo, run }: { repo: string; run: Run }) {
  const nodes = toNodes(run.jobs);
  const [open, setOpen] = useState<Set<string>>(() => new Set(nodes.filter((n) => n.matrix && failed(n.status)).map((n) => n.id)));
  const toggle = (id: string) => setOpen((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set([...s, id])));
  const [ref, avail] = useWidth<HTMLDivElement>();
  const { placed, w, width, height } = layout(nodes, open, avail || Number.POSITIVE_INFINITY);
  const at = new Map(placed.map((p) => [p.id, p]));
  return (
    <div ref={ref} className="overflow-x-auto bg-inset p-4">
      <div className="relative" style={{ width, height }}>
        <svg width={width} height={height} className="pointer-events-none absolute inset-0 overflow-visible" aria-hidden>
          {placed.flatMap((n) =>
            n.needs.map((id) => {
              const from = at.get(id)!;
              const x1 = from.x + w;
              const y1 = from.y + HEAD / 2;
              const x2 = n.x;
              const y2 = n.y + HEAD / 2;
              const mx = (x1 + x2) / 2;
              return (
                <path
                  key={`${id}->${n.id}`}
                  className={cn("fill-none stroke-2", EDGE_CLASS[from.status])}
                  d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                />
              );
            }),
          )}
        </svg>
        {placed.map((n) => (
          <NodeCard key={n.id} repo={repo} n={n} w={w} open={open.has(n.id)} toggle={() => toggle(n.id)} />
        ))}
      </div>
    </div>
  );
}

const nodeHead = "flex h-[38px] shrink-0 items-center gap-2 px-2.5 text-left";
const nodeName = "min-w-0 flex-1 truncate font-semibold";

function NodeCard({ repo, n, w, open, toggle }: { repo: string; n: Placed; w: number; open: boolean; toggle: () => void }) {
  const style = { left: n.x, top: n.y, width: w, height: n.h };
  const dim = n.status === "waiting" || n.status === "skipped";
  const card = cn(
    "absolute flex flex-col overflow-hidden rounded-md border bg-background text-foreground",
    dim && "opacity-60",
    failed(n.status) && "border-danger-muted",
  );
  if (!n.matrix) {
    const e = n.jobs[0];
    const why = e.status === "skipped" ? blockedBy(e) : null;
    const want = waitingFor(e);
    const state = want ? `waiting: no live runner offers ${want.join(", ")}` : STATUS_WORD[e.status];
    return (
      <Tooltip content={`${e.job}: ${state}${why ? ` (needs ${why})` : ""}${e.attempt > 1 ? `, attempt ${e.attempt}` : ""}`}>
        <Link className={cn(card, "hover:border-faint hover:no-underline")} style={style} to={logHref(repo, e)}>
          <div className={nodeHead}>
            <EvidenceIcon status={e.status} />
            <span className={nodeName}>{e.job}</span>
            {e.attempt > 1 && <span className="text-xs text-muted-foreground">#{e.attempt}</span>}
            <span className="text-xs text-muted-foreground">
              {why ? "blocked" : want ? "no runner" : e.ms !== null ? duration(e.ms) : STATUS_WORD[e.status]}
            </span>
          </div>
        </Link>
      </Tooltip>
    );
  }
  const done = n.jobs.filter((e) => e.status === "pass").length;
  return (
    <div className={card} style={style}>
      <Tooltip content={`${n.name}: ${n.jobs.length} matrix jobs`}>
        <button className={cn(nodeHead, "hover:bg-muted")} onClick={toggle} aria-expanded={open}>
          <EvidenceIcon status={n.status} />
          <span className={nodeName}>
            <span className="text-muted-foreground">Matrix:</span> {n.name}
          </span>
          <span className="text-xs text-muted-foreground">
            {done}/{n.jobs.length}
          </span>
          {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </button>
      </Tooltip>
      {open && (
        <ul className="border-t border-border-muted pb-2">
          {n.jobs.map((e) => (
            <li key={e.job}>
              <Tooltip content={`${e.job}: ${STATUS_WORD[e.status]}`} side="right">
                <Link className="flex h-[30px] items-center gap-2 pl-[18px] pr-2.5 text-foreground hover:bg-muted hover:no-underline" to={logHref(repo, e)}>
                  <EvidenceIcon status={e.status} />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">{e.job.slice(n.name.length).replace(/^ \((.*)\)$/, "$1")}</span>
                  <span className="text-xs text-muted-foreground">{e.ms !== null ? duration(e.ms) : STATUS_WORD[e.status]}</span>
                </Link>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CiRunPage() {
  const { repo = "", subject = "", sha = "" } = useParams();
  const q = useCiRun(repo, subject, sha);
  const verify = subject.startsWith("verify:");
  return (
    <Query q={q}>
      {(run) => {
        const status = rollup(run.jobs);
        const ms = run.jobs.reduce((s, e) => s + (e.ms ?? 0), 0);
        const counts = run.jobs.reduce<Record<string, number>>((c, e) => ((c[e.status] = (c[e.status] ?? 0) + 1), c), {});
        const first = Math.min(...run.jobs.map((e) => e.queued_at));
        const end = Math.max(...run.jobs.map((e) => e.queued_at + (e.ms ?? 0)));
        const running = run.jobs.some((e) => e.status === "queued" || e.status === "waiting");
        const mixed = Object.keys(counts).length > 1;
        return (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-2xl font-normal">
                <EvidenceIcon status={status} />
                <span className="min-w-0 truncate">{verify ? "Post-land verification" : (run.change?.subject ?? `Change ${subject.slice(-8)}`)}</span>
                {run.change && <StateLabel state={run.change.state} />}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                {verify ? (
                  <Link className="inline-flex items-center gap-1" to={`${rp(repo)}/ci?view=verify`}>
                    <VerifiedIcon /> probe on main
                  </Link>
                ) : (
                  <Link className="inline-flex items-center gap-1 font-mono" to={`${rp(repo)}/changes/${subject}`}>
                    <GitPullRequestIcon /> #{subject.slice(-8)}
                  </Link>
                )}
                <ListItemSeparator />
                <Sha repo={repo} sha={run.commit} n={7} />
                {run.change && (
                  <>
                    <ListItemSeparator /> by {run.change.agent_id}
                  </>
                )}
                <ListItemSeparator /> started <TimeAgo ms={first} />
              </div>
            </div>
            <dl className="flex flex-wrap gap-x-10 gap-y-3 rounded-md border px-4 py-3 [&>div]:flex [&>div]:flex-col [&>div]:gap-0.5 [&_dt]:text-xs [&_dt]:text-muted-foreground [&_dd]:font-semibold">
              <div>
                <dt>Status</dt>
                <dd className="capitalize">{STATUS_WORD[status]}</dd>
              </div>
              <div>
                <Tooltip content="First job queued to last job finished">
                  <dt className="self-start">Duration</dt>
                </Tooltip>
                <dd>{running ? "–" : duration(end - first)}</dd>
              </div>
              <div>
                <Tooltip content="Sum of every job's run time">
                  <dt className="self-start">Job time</dt>
                </Tooltip>
                <dd>{duration(ms) || "–"}</dd>
              </div>
              <div>
                <dt>Jobs</dt>
                <dd className="flex flex-wrap items-center gap-2.5">
                  {run.jobs.length}
                  {mixed &&
                    (Object.keys(STATUS_WORD) as Status[])
                      .filter((s) => counts[s])
                      .map((s) => (
                        <span key={s} className="flex items-center gap-1 font-normal text-muted-foreground">
                          <EvidenceIcon status={s} /> {counts[s]} {STATUS_WORD[s]}
                        </span>
                      ))}
                </dd>
              </div>
            </dl>
            <Card className="mt-0">
              <CardHeader>
                <WorkflowIcon /> Job graph{" "}
                <span className="ml-auto text-xs font-normal text-muted-foreground">Click a matrix to expand it, a job to open its log</span>
              </CardHeader>
              <Graph repo={repo} run={run} />
            </Card>
          </div>
        );
      }}
    </Query>
  );
}
